<template>
	<template v-if="visible">
		<div v-if="ro" class="html-area read-only" :class="[clazz, errorClass, displayClass]" :style="[style, extraStyles]" v-html="value"></div>
		<template v-else>
			<template v-if="!isEmbedded">
				<anchor-element :chain="chain" tag="div" class="html-area"
								:class="[clazz, errorClass, displayClass, ro ? 'read-only':null, toolbarVisible ? 'like-focus':null]"
								:style="[style, extraStyles]" v-bind="attrs"
								@mousedown="stopBubbling"
								ref="anchor"></anchor-element>
				<boat-element :chain="chain" :tag="ToolBar" :toolbar="toolbar" @action="toolbarAction" :vertical-correction="true"></boat-element>
			</template>
			<template v-else>
				<div class="html-area-embedded" :class="{ boxed: isBoxed }" v-bind="inheritedAttrs">
					<tool-bar :toolbar="toolbar" @action="toolbarAction" :class="{ embedded: isEmbedded }"></tool-bar>
					<div class="html-area" :class="[clazz, errorClass, displayClass, ro ? 'read-only':null]" :style="[style, extraStyles]" v-bind="attrs"
									@mousedown="stopBubbling" ref="editor"></div>
				</div>
			</template>
			<ui-root v-if="isDialog" :part="dialogForm" :data="dialogData" :readonly="false" :viewmode="false"
					 :display="<ModalDialogDisplay>{ mode: 'modal', moveable: true, resizable: true }"
					 :actions="dialogControls(env, ['cancel', 'ok'])"
					 @close="dialogClosed"></ui-root>
		</template>
	</template>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {
	computed,
	nextTick,
	ref,
	watch,
	onUnmounted,
	useAttrs,
	type CSSProperties
} from "vue";
import type {HtmlArea} from "@t/fd/editor/HtmlArea.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {closeAll, createChain} from "@/ui/paneltree/PanelTree.ts";
import ToolBar from "@/ui/toolbar/ToolBar.vue";
import {BlockTypeChange, buildToolbar, findComponent, Toolbar, ToolbarMenu, ToolbarMenuItem, ToolbarMenuItems} from "@t/ui/Toolbar.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
//import {ProsemirrorAdapterProvider} from "@prosemirror-adapter/vue";
import {fromString} from "@fd/editors/html/Utils.ts";
import {EditorState, type Command, Selection} from "prosemirror-state";
import {DOMParser, DOMSerializer, type Node} from "prosemirror-model";
import {EditorView} from "prosemirror-view";
import {editorSupport, UiCommand} from "@fd/editors/html/EditorSupport.ts";
import type {FormData} from "@t/fd/FormPart.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {ModalDialogDisplay} from "@t/ui/Dialog.ts";
import {useTemplateStore} from "@/store/TemplateStore.ts";
import {SelectHierarchy, SelectHierarchyItem} from "@t/ui/SelectHierarchy.ts";
import {ApplyTemplateResponse, TemplateVariables} from "@t/template/Templates.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {plaintextToHtml} from "@/utils/Html.ts";
import {getter, setter} from "@fd/Part.ts";
import {ImageNodeView} from "@fd/editors/html/components/ImageNodeView.ts";
import {undo, redo} from "prosemirror-history";
import {injectContext} from "@t/fd/AppContext.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const area = computed<HtmlArea>(() => props.part as HtmlArea);
const isEmbedded = computed<boolean>(() => area.value?.toolbarMode === 'embedded');
const isBoxed = computed<boolean>(() => area.value.displayStyle === 'box');
const {visible, value, clazz, errorClass, style, ro, vm, tabindexBinding, formData, getValueOfProperty, setValueOfProperty } = useEditor(props);
const inheritedAttrs = useAttrs();

const env = injectEnv();
const bus = connectEventBus("HtmlAreaPart."+area.value.property)
const parentBus = bus.parent;

const ts = useTemplateStore();
const ctx = injectContext();
const anchor = ref<InstanceType<typeof AnchorElement>>(null)
const editor = ref<HTMLDivElement|null>(null)
const chain = createChain({
	managed: true,	/* Cannot be autoclosed */
	rule: (m:ElementMeasure) => {

		return {
			ah: 0, av: 0,
			bh: 0, bv: 100,
			x: 0, y: -4
		};
	}
}, "html-toolbar");

const editorElement = computed<HTMLElement|null>(() => {
	if (isEmbedded.value) {
		return editor.value;
	} else {
		return anchor.value ? anchor.value.element() : null;
	}
});

let resizeObserver:ResizeObserver = null;
const areaWidth = ref<number>(area.value.width ?? -1);
const areaHeight = ref<number>(area.value.height ?? -1);

const inFocus = ref<boolean>(false);
const isDialog = ref<boolean>(false);
const dialogData = ref<FormData>({});
const dialogForm = ref<InterfaceRoot<FormDefinition>|null>(null);

let wasEdit = false;

const supp = editorSupport(env, (state) => {
	closeToolbarDropdowns();
});

function closeToolbarDropdowns() {
	// close drop menu if any open
	if (isEmbedded.value) closeAll();	// dropdowns are registered to page floor in embedded mode
	else chain.closeChildren();			// but they are part of toolbar chain in floating mode
}

const extraStyles = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	if (area.value.resize) s.resize = area.value.resize;
	if (areaWidth.value > 0) s.width = areaWidth.value + "px";
	if (areaHeight.value > 0) s.height = areaHeight.value + "px";
	return s;
});

const displayClass = computed<string>(() => {
	if (isBoxed.value || (isEmbedded.value && !ro.value)) return "undecorated";
	else return "likeInput";
});

function selectItemToMenuItem(shi:SelectHierarchyItem):ToolbarMenuItem {
	const tmi:ToolbarMenuItem = {
		type: "menuitem",
		id: "template-"+shi.id,
		title: shi.text
	};
	if (shi.submenu && Array.isArray(shi.submenu.items)) {
		tmi.children = [];
		for (let child of shi.submenu.items) {
			tmi.children.push(selectItemToMenuItem(child));
		}
	}
	return tmi;
}

async function templateMenuBuilder():Promise<ToolbarMenuItems> {
	const temps:SelectHierarchy = await ts.loadApplicableTemplates(area.value.templatesFor, area.value.templatesForSpec);
	// Convert SelectHierarchy to menu items
	const items:ToolbarMenuItems = [];
	if (temps.help) {
		items.push({
			id: "template-help",
			type: "menuitem",
			title: temps.help,
			disabled: true
		});
	}
	for (let shi of temps.items) {
		items.push(selectItemToMenuItem(shi));
	}
	return items;
}

const toolbar = computed<Toolbar>(() => {
	const { blockType, selectionEmpty, selectionWithLinks } = supp;
	const bar = buildToolbar(env.msg, (tc) => {
		if (tc.id === "justifyMenu") {
			const allowed = blockType.value === "p" || /h\d/g.test(blockType.value);
			if (allowed) {
				let hint = supp.alignHint.value;
				switch (hint) {
					case "left":
						(tc as ToolbarMenu).icon = "align-left";
						break;
					case "center":
						(tc as ToolbarMenu).icon = "align-center";
						break;
					case "right":
						(tc as ToolbarMenu).icon = "align-right";
						break;
					case "justify":
						(tc as ToolbarMenu).icon = "align-justify";
						break;
				}
			}
			return allowed;
		} else if (["p", "h1", "h2", "h3", "h4", "h5", "h6", "pre"].includes(tc.id)) {
			return blockType.value !== "dt" && blockType.value !== "dd";
		}
		switch (tc.id) {
			case "dt":
			case "dd":
				return blockType.value === "dt" || blockType.value === "dd";
			case "link":
				return !selectionEmpty.value && !selectionWithLinks.value;
			case "unlink":
				return selectionWithLinks.value;
			case "olist":
				return supp.canOrderedList.value;
			case "ulist":
				return supp.canBulletList.value;
			case "dlist":
				return supp.canDescList.value;
			case "indent":
				return supp.canIndent.value;
			case "outdent":
				return supp.canOutdent.value;
			case "blockquote":
				return supp.canBlockquote.value;
			case "insertTable":
				return !supp.canDeleteTable.value;
			case "tableMenu":
			case "deleteTable":
			case "addTable":
			case "insertColumnBefore":
			case "insertColumnAfter":
			case "insertRowBefore":
			case "insertRowAfter":
			case "deleteColumn":
			case "deleteRow":
			case "toggleHeaderRow":
			case "toggleHeaderColumn":
			case "toggleHeaderCell":
				return supp.canDeleteTable.value;
			case "mergeCells":
				return supp.canMergeCells.value;
			case "splitCell":
				return supp.canSplitCell.value;
			case "history":
				return supp.canUndo.value || supp.canRedo.value;
			case "undo":
				return supp.canUndo.value;
			case "redo":
				return supp.canRedo.value;
		}
		if (tc.id === "applyTemplate") {
			if (area.value.templatesFor) (tc as ToolbarMenu).items = templateMenuBuilder;
			else return false;	// not enabled
		}
		return true;
	});
	const styleMenu = findComponent(bar, "block_style_menu") as ToolbarMenu;
	if (styleMenu) {
		const styleItem = findComponent(bar, blockType.value) as ToolbarMenuItem;
		if (styleItem) styleMenu.title = styleItem.title;
	}
	return bar;
});

const toolbarVisible = computed<boolean>(() => (inFocus.value || chain.interacting.value) && !isDialog.value);
watch(toolbarVisible, (vis) => {
	if (vis) chain.show();
	else chain.hide();
})

const attrs = computed(() => {
	const a: any = tabindexBinding();
	//if (!ro.value && !vm.value) a.contenteditable = "";
	return a;
});


let view:EditorView = null;

function saveContent(state:EditorState) {
	const serializer = DOMSerializer.fromSchema(state.schema);
	const fragment = serializer.serializeFragment(state.doc.content);
	const div = document.createElement("div");
	div.appendChild(fragment);
	//console.log("HTML = " + div.innerHTML);
	value.value = div.innerHTML;
	wasEdit = false;
}

function updateDocVersion() {
	let vp = area.value.versionProperty;
	if (!vp) return;
	const current = getValueOfProperty(vp);
	if (typeof current === 'number') setValueOfProperty(vp, current + 1);
	else setValueOfProperty(vp, 1);
}

function editorElementSetup(el:HTMLElement) {
	startObservingSize(el);

	const state:EditorState = EditorState.create({
		doc: DOMParser.fromSchema(supp.schema).parse(fromString(value.value)),
		schema: supp.schema,
		plugins: [ ...supp.plugins ]
	});

	view = new EditorView(el, {
		state,
		dispatchTransaction(tr) {
			wasEdit = wasEdit || tr.docChanged;
			if (tr.docChanged) updateDocVersion();
			let newState = view.state.apply(tr);
			view.updateState(newState);
			if (wasEdit && !inFocus.value) {
				// Update HTML value if the editor isn't in focus to handle cases like D&D
				saveContent(newState);
			}
		},
		handleDOMEvents: {
			focus: ()=> {
				inFocus.value = true;
			},

			blur: (view:EditorView) => {
				inFocus.value = false;
				if (wasEdit) saveContent(view.state);
			}
		},
		nodeViews: {
			image(node:Node, view:EditorView, getPos: ()=>number) {
				return new ImageNodeView(node, view, getPos);
			}
		}
	});
	// We have our own table editing
	document.execCommand('enableObjectResizing', false, 'false');
	document.execCommand('enableInlineTableEditing', false, 'false');
}

function editorElementDestoy() {
	view?.destroy();
	view = null;
	stopObservingSize();
}

watch(editorElement, (newEl:HTMLElement, oldEl:HTMLElement) => {
	if (oldEl && view) {
		editorElementDestoy();
	}
	if (newEl) {
		editorElementSetup(newEl);
	}
}, { flush: 'post' });

function applyCommand(command:Command):boolean {
	if (view) {
		let success = command(view.state, view.dispatch, view);
		if (success) supp.updateToolbar(view.state);
		return success;
	}
	return false;
}

function applyBlockTypeChange(bt:BlockTypeChange) {
	return applyCommand(supp.modifyBlockType(bt.node, bt.attrs));
}

let lastUiCommand:UiCommand<any> = null;

function applyUiCommand<D>(command:UiCommand<D>):boolean {
	if (view) {
		savedSelection = view.state.selection.toJSON();
		const data: D = command.extract(view.state);
		if (data) {
			dialogData.value = data;
			dialogForm.value = command.form;
			isDialog.value = true;	// show the dialog
			lastUiCommand = command;
			return true;
		}
	}
	return false;
}

let savedSelection:any = null;

function dialogClosed(selectionRestore = true) {
	// Clean Up and Close
	dialogData.value = null;
	dialogForm.value = null;
	isDialog.value = false;
	lastUiCommand = null;
	nextTick(() => {
		if (view) {
			view.focus();
			if (selectionRestore && savedSelection) {
				const cmd:Command = (state:EditorState, dispatch?):boolean => {
					const tr = state.tr;
					tr.setSelection(Selection.fromJSON(tr.doc, savedSelection));
					if (dispatch) dispatch(tr);
					return true;
				}
			}
		}
	});
}

bus.onAnyCommand(async (cmd:string) => {
	if (cmd === "ok" && lastUiCommand && view) {
		const cmd:Command = lastUiCommand.prepare(dialogData.value, savedSelection);
		const ok = cmd(view.state, view.dispatch, view);
		dialogClosed(false /* Restored by action command */);
		return ok ? "ok" : "fail";
	}
	dialogClosed(false /* Restored by action command */);
	return "pass";
});

parentBus.on("focus", (editorName:string) => {
	if (area.value.name === editorName) {
		nextTick(() => {
			if (view) view.focus();
		});
	}
});

parentBus.on("flush", (editorName:string) => {
	if (area.value.name === editorName) saveContent(view.state);
});

parentBus.on("update", (editorName:string) => {
	if (area.value.name === editorName) {
		if (!view) return;
		const schema = view.state.schema;

		// Parse HTML from value.value the same way as on init
		const newDoc = DOMParser
			.fromSchema(schema)
			.parse(fromString(value.value));

		const tr = view.state.tr
			.replaceWith(
				0,
				view.state.doc.content.size,
				newDoc.content
			);

		view.dispatch(tr);
		supp.updateToolbar(view.state);
	}
});

function applyTemplate(templateId: number):boolean {
	if (view) {
		const selected = supp.getSelectedHTML(view.state);

		const serializer = DOMSerializer.fromSchema(view.state.schema);
		const fragment = serializer.serializeFragment(view.state.doc.content);
		const div = document.createElement("div");
		div.appendChild(fragment);
		const body = div.innerHTML;

		const vars:TemplateVariables = { selected, body };

		ts.applyTemplate(templateId, vars, ctx.value)
			.then((data:ApplyTemplateResponse) => {
				let html = htmlize(data.output, data.contentType);
				applyCommand(supp.insertHTML(html));
				populateProperties(data.properties)
			});
		return true;
	}
	return false;
}

function populateProperties(data?: TemplateVariables):void {
	if (!data) return;
	for (let key of Object.keys(data)) {
		let val = data[key];
		const current = getter(formData, key, formData);
		//console.log(key + " = " + JSON.stringify(val) + ", current = " + current);
		//hack - remove at CRM 7.5 - subject se ma menit jen kdyz neni prazdny, 
		if (key === 'subject' && current) {
			console.log("Skip setting property subject , not empty value:"+current+" - remove this hack at CRM 7.5");
		}
		else {
			if (!Array.isArray(val)) {
				val = castByCurrent(val.toString(), current);
			}
			setter(formData, key, val, formData);
		}
	}
}

function castByCurrent(newVal:string, oldVal:any):any {
	if (oldVal == null) return newVal;	// no casting possible
	if (typeof oldVal === "boolean") {
		return newVal === "true";
	} else if (typeof oldVal === "number") {
		return parseInt(newVal);
	} else if (typeof oldVal === "string") {
		return newVal;
	} else if (Array.isArray(oldVal)) {
		// Split by comma
		let strings = newVal.split(',');
		for (let i = 0; i < strings.length; i++) {
			strings[i] = strings[i].trim();
		}
		return strings;
	}
	return oldVal;	// unsupported type
}

function htmlize(text:string, mime:string):string {
	if (mime.startsWith('text/plain')) {
		// Convert plaintext to a html
		text = plaintextToHtml(text, { linkify:true });
	}
	return text;
}

function toolbarAction(id: string, arg?: any):boolean {
	if (id.startsWith("template-")) return applyTemplate(parseInt(id.substring(9)))
	switch (id) {
		case "bold":
			return applyCommand(supp.bold);
		case "italic":
			return applyCommand(supp.italic);
		case "underline":
			return applyCommand(supp.underline);
		case "strike":
			return applyCommand(supp.strike);
		case "subscript":
			return applyCommand(supp.subscript);
		case "superscript":
			return applyCommand(supp.superscript);
		case "code":
			return applyCommand(supp.code);
		case "kbd":
			return applyCommand(supp.kbd);
		case "var":
			return applyCommand(supp.variable);
		case "justifyLeft":
			return applyCommand(supp.alignLeft);
		case "justifyCenter":
			return applyCommand(supp.alignCenter);
		case "justifyRight":
			return applyCommand(supp.alignRight);
		case "justifyFull":
			return applyCommand(supp.alignJustify);
		case "removeFormat":
			return applyCommand(supp.removeFormat);
		case "unlink":
			return applyCommand(supp.unlink);
		case "link":
			return applyUiCommand(supp.link);
		case "mark":
			return applyCommand(supp.mark);
		case "p":
		case "pre":
		case "h1":
		case "h2":
		case "h3":
		case "h4":
		case "h5":
		case "h6":
		case "dt":
		case "dd":
			return applyBlockTypeChange(arg as BlockTypeChange);
		case "olist":
			return applyCommand(supp.orderedList);
		case "ulist":
			return applyCommand(supp.bulletList);
		case "dlist":
			return applyCommand(supp.descList);
		case "outdent":
			return applyCommand(supp.outdent);
		case "indent":
			return applyCommand(supp.indent);
		case "blockquote":
			return applyCommand(supp.blockquote);
		case "color":
			return applyCommand(supp.setColor(arg));
		case "hr":
			return applyCommand(supp.insertHorizontalRule);
		case "insertTable":
		case "addTable":
			return applyCommand(supp.insertTable);
		case "deleteTable":
			return applyCommand(supp.deleteTable);
		case "insertColumnBefore":
			return applyCommand(supp.addColumnBefore);
		case "insertColumnAfter":
			return applyCommand(supp.addColumnAfter);
		case "insertRowBefore":
			return applyCommand(supp.addRowBefore);
		case "insertRowAfter":
			return applyCommand(supp.addRowAfter);
		case "deleteColumn":
			return applyCommand(supp.deleteColumn);
		case "deleteRow":
			return applyCommand(supp.deleteRow);
		case "mergeCells":
			return applyCommand(supp.mergeCells);
		case "splitCell":
			return applyCommand(supp.splitCell);
		case "toggleHeaderRow":
			return applyCommand(supp.toggleHeaderRow);
		case "toggleHeaderColumn":
			return applyCommand(supp.toggleHeaderColumn);
		case "toggleHeaderCell":
			return applyCommand(supp.toggleHeaderCell);
		case "insertImage":
			return applyUiCommand(supp.insertImage);
		case "undo":
			return applyCommand(undo);
		case "redo":
			return applyCommand(redo);
		default:
			console.log("Action '" + id + "' not yet supported (arg = " + arg + ")");
	}
}


function resizeCallback(entries:ResizeObserverEntry[]) {
	if (entries.length > 0) {
		const editorWrapper = entries[0].target as HTMLElement;
		const m = getElementMeasure(editorWrapper);
		if (area.value.resize === "both" || area.value.resize === "vertical") {
			areaHeight.value = m.height;
		}
		if (area.value.resize === "both" || area.value.resize === "horizontal") {
			areaWidth.value = m.width;
		}
	}
}

function startObservingSize(el:HTMLElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(resizeCallback);
		resizeObserver.observe(el);
	}
}

function stopObservingSize() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
}

function stopBubbling(e:MouseEvent) {
	e.stopPropagation();
}

onUnmounted(() => {
	stopObservingSize();
});
</script>

<style>
/* Wrapper around toolbar and editor in embedded mode */
.html-area-embedded {
	display: grid;
	grid-template-rows: max-content auto;
	align-items: stretch;
	overflow: hidden;
	width: 100%;

	&:not(.boxed) {
		border-radius: var(--input-radius);
		box-shadow: var(--input-shadow);

		&:focus,
		&:focus-visible,
		&:focus-within,
		&.like-focus {
			outline: none;
			box-shadow: 0 0 2px 2px var(--outline-color);
		}
	}

	& > .html-area {
		padding-inline: var(--input-h-padding);
		overflow-y: auto;
	}
}

.html-area {
	line-height: var(--input-height);
	min-height: var(--input-height);
	max-height: none;
	width: 100%;
	height: auto;
	overflow: auto;
	color: var(--fg);
	background-color: var(--bg);

	&.likeInput {
		height: auto;
	}

	/* Synchronized with ckeditor.css.txt */
	& p, & h1, & h2, & h3, & h4, & h5, & h6 {
		margin: 0;
		text-align: left;
	}
	& p, & dt, & dd {
		margin-block: 5px;
		line-height: 20px;
	}

	& h1 {
		font-size: 29px;
		margin-top: 10px	;
		margin-bottom: 5px;
	}

	& h2 {
		font-size: 22px;
		margin-top: 10px;
		margin-bottom: 5px;
	}
	& h3 {
		font-size: 17px;
		margin-top: 8px	;
		margin-bottom: 3px;
	}
	& h4 {
		font-size: 15px;
		margin-top: 8px	;
		margin-bottom: 3px;
	}
	& h5, & h6 {
		font-size: 14px;
		margin-top: 5px	;
		margin-bottom: 2px;
	}
	& ul, & ol {
		margin-left: 1.5rem;
		padding-left: 0;
	}
	& ul, & ol, & dl {
		margin-block: 5px;
	}


	/* Paragraphs inside list items */
	& li {
		padding-left: 0;
		line-height: 20px;
		& > p {
			margin-block: 0;
		}
	}

	& code {
		color: color-mix(in srgb, var(--fg) 66%, red);
	}

	& pre {
		background-color: #6699FF22;
		padding: 4px 8px;
		border-radius: 4px;

		& > code {
			background-color: transparent;
			color: var(--fg);
		}
	}

	& kbd {
		outline: 1px solid rgb(from var(--fg) r g b / 0.2);
		padding-inline: 2px;
		border-radius: 3px;
		background-color: rgb(from var(--fg) r g b / 0.03);
	}

	& var {
		color: color-mix(in srgb, var(--fg) 50%, lightseagreen);
		font-style: normal;
		font-family: var(--font-mono);
	}

	& dt {
		font-weight: bold;
		margin-bottom: 0;
	}

	& table {
		--default-cell-min-width: 20px;
		border-collapse: collapse;
		table-layout: fixed;
		overflow: hidden;
		border-left: 1px solid rgb(from var(--fg) r g b / 0.2);
		border-top: 1px solid rgb(from var(--fg) r g b / 0.2);
		margin-bottom: 8px;

		& > tbody {
			& > tr {
				& > td, & > th {
					padding: 4px 8px;
					min-width: 20px;
					text-align: left;
					vertical-align: top;
					box-sizing: border-box;
					position:relative;
					border-right: 1px solid rgb(from var(--fg) r g b / 0.2);
					border-bottom: 1px solid rgb(from var(--fg) r g b / 0.2);

					&:not([data-colwidth]):not(.column-resize-dragging) {
						/* if there's no explicit width set and the column is not being resized, set a default width */
						min-width: var(--default-cell-min-width);
					}

					& > p {
						margin-block: 0;
					}
				}
				& > th {
					background-color: rgb(from var(--fg) r g b / 0.05);
					color: var(--fg);
				}
			}
		}
	}

	& > .ProseMirror {
		/* Inner prose mirror element takes all available height of the wrapper */
		min-height: 100%;
		display: flow-root;	/* Prevent leaking block margins outside the editor */
	}
}

.html-debug {
	font-size: 10px;
	line-height: 12px;
	font-family: var(--font-mono);
	max-width: 600px;
	padding-inline: 8px;
	word-break: break-all;
}

.ProseMirror {
	&:focus, &:focus-visible {
		outline:none;
	}

	& .tableWrapper {
		overflow-x: auto;
	}

	& .column-resize-handle {
		position: absolute;
		right: -2px;
		top: 0;
		bottom: 0;
		width: 4px;
		z-index: 20;
		background-color: var(--selection-bg);
		pointer-events: none;
	}

	&.resize-cursor {
		cursor: col-resize;
	}

	/* Give selected cells overlay */
	& .selectedCell:after {
		z-index: 2;
		position: absolute;
		content: '';
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		background: rgb(from var(--selection-bg) r g b / 0.2);
		pointer-events: none;
	}

	/* Eliminate bottom margin of the last paragraph of the editor (PM don't focus when clicking on the margin) */
	& > p:last-child {
		margin-block-end: 0;
		padding-bottom: 1.5rem;	/* This is not necessary but it is here to give the user a visual clue that this is the end of the document. */
	}
}

.shadow-selection {
	background-color: color-mix(in srgb, var(--selection-bg) 50%, var(--bg));
}

/* ImageNodeView - our custom image representation */
.img-wrapper {
	display: inline-block;
	position: relative;
	vertical-align: baseline;
	line-height: 0; /* odstraní nechtěný mezerový offset */
	user-select: none;
	-webkit-user-select: none;
	--handle-color: transparent;

	& * {
		user-select: none;
		-webkit-user-select: none;
	}

	.ProseMirror-focused & {
		--handle-color: var(--selection-bg);
	}

	&.ProseMirror-selectednode {
		outline: 2px solid var(--handle-color);
	}

	/* Scale buttons container */
	& > .img-scale-buttons {
		position: absolute;
		top: 16px;
		left: 16px;
		right: 16px;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		gap: 4px;
		visibility: hidden;

		& > .img-scale-button {
			background-color: #00000080;
			outline: 1px solid #FFFFFF66;
			color: white;
			font-size: var(--font-size);
			line-height: 16px;
			padding-inline: 6px;
			border-radius: 5px;
			cursor: pointer;

			&:hover {
				background-color: #00000099;
			}
			&:active {
				background-color: #000000AA;
			}
		}
		.ProseMirror-focused & {
			visibility: visible;
		}
	}

	& > .img-size-info {
		background-color: black;
		color: white;
		font-size: 10px;
		padding-inline: 8px;
		line-height: 16px;
		border-radius: 5px;
		position: absolute;
		right: 16px;
		bottom: 16px;
		opacity: 0;

		.ProseMirror-focused & {
			opacity: 0.8;
		}
	}

	& > .img-selection-box {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border: 1px solid var(--handle-color);
		background-color: color-mix(in srgb, var(--handle-color) 10%, transparent);
		pointer-events: none;
	}

	& > .img-corner-handle,
	& > .img-right-handle,
	& > .img-bottom-handle {
		position: absolute;
		background-color: var(--handle-color);
	}

	& > .img-corner-handle {
		width: 16px;
		height: 16px;
		right: -8px;
		bottom: -8px;
		cursor: nwse-resize;
		border-radius: 50%;
	}

	& > .img-right-handle {
		width: 12px;
		height: 40%;
		top: 30%;
		right: -6px;
		cursor: ew-resize;
		border-radius: 6px;
	}

	& > .img-bottom-handle {
		width: 40%;
		height: 12px;
		bottom: -6px;
		left: 30%;
		cursor: ns-resize;
		border-radius: 6px;
	}
}
</style>